/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TestCaseEntity } from '../models/test-case-entity';
import { TestCasePresentation } from '../models/test-case-presentation';
import { Router, RouterLink } from '@angular/router';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { TestToolService } from '../services/test-tool.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { CurrentUserService } from '../../user/services';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, DxiValidationRuleModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-test-tool-test-cases',
    templateUrl: './test-tool-test-cases.component.html',
    styleUrls: ['./test-tool-test-cases.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SvgIconComponent, PermissionRequiredDirective, RouterLink, DxDataGridModule, 
        BizDataGridDirective, DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, 
        DxiColumnModule, DxiValidationRuleModule, BizDateColumnDirective, 
        BizUserColumnDirective, DxiButtonModule, DxTemplateModule
    ]
})
export class TestToolTestCasesComponent implements OnInit, AfterViewInit {
    public bizGridFiltervalues = inject(BizGridFiltervalues);
    
    private router = inject(Router);
    private loadingPanelService = inject(LoadingPanelService);
    private testToolService = inject(TestToolService);
    private toastService = inject(ToastService);
    private currentUserService = inject(CurrentUserService);

    allowAdding = false;
    allowUpdating = false;
    allowDeleting = false;

    testCases: TestCasePresentation[];

    ngAfterViewInit(): void {
        void this.refresh();
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('TestTool_Action_TestCase_Create');
        this.allowUpdating = this.currentUserService.hasPermission('TestTool_Action_TestCase_Edit');
        this.allowDeleting = this.currentUserService.hasPermission('TestTool_Action_TestCase_Delete');
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            await Promise.all([
                this.testToolService.getTestCases().then(result => { 
                    this.testCases = result.map(testCase => ({
                        ...testCase,
                        PreRunValidationDescription: testCase.PreRunValidationDescription ?? 'Ready'
                    }));
                }),
            ]);
        }
        catch {
            this.toastService.showToast('Failed to load data...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    onEditorPreparing(e: any): void {
        if (e.dataField === 'PreRunValidationDescription') {
            e.editorOptions.disabled = true;
        }
    }

    async onRowInsertedOrUpdated(e: any): Promise<void> {
        const item = e.data as TestCaseEntity;
        const newItem = await this.saveTestCase(item);
        if (newItem) {
            item.Id = newItem.Id;
        }
    }

    async onRowRemoving(e: any): Promise<void> {
        const testCase = e.data as TestCasePresentation;
        await this.testToolService.deleteTestCase(testCase.Id);
    }

    onInitNewRow = (): void => {
        void this.router.navigate(['/test-tool', 'create-test-case']);
    }

    onRowDblClick(e: any): void {
        const testCase = e.data as TestCasePresentation;
        this.editTestCaseInterchangesInternal(testCase);
    }

    editTestCaseInterchanges = (e: any) => {
        const testCase = e.row.data as TestCasePresentation;
        this.editTestCaseInterchangesInternal(testCase);
    }
    
    editTestCaseInterchangesInternal(testCase: TestCasePresentation) {
        void this.router.navigate(['/test-tool/test-cases/test-case-interchanges', {testCaseId: testCase.Id, testCaseName: testCase.Name}]);
    }

    async saveTestCase(item: TestCaseEntity): Promise<TestCaseEntity> {
        try {
            const newItem = await this.testToolService.saveTestCase(item);
            this.toastService.showToast('Test case saved...', ToastType.success);
            return newItem;
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to save test case ...', ToastType.error);
            return null;
        }
    }

    nameValidation = async (params: any): Promise<boolean> => {
        const existingTestCase = this.testCases.find(tc => tc.Id === params.data.Id);
        if (existingTestCase && existingTestCase.Name === params.value) {
            return true;
        }

        const isAvailable = await this.testToolService.isTestCaseNameAvailable(params.value);
        if (!isAvailable) {
            throw new Error('Name is not available');
        }

        return true;
    }
}
